import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { BookClubDiscussion, BookClubDiscussionReply } from '@/types/chat';

interface UseBookClubDiscussionsResult {
  discussions: BookClubDiscussion[];
  loading: boolean;
  createDiscussion: (data: {
    type: 'prompt' | 'chapter';
    title: string;
    description?: string;
    chapter_start?: number;
    chapter_end?: number;
    is_spoiler?: boolean;
  }) => Promise<void>;
  deleteDiscussion: (id: string) => Promise<void>;
  fetchReplies: (discussionId: string) => Promise<BookClubDiscussionReply[]>;
  addReply: (discussionId: string, content: string) => Promise<void>;
  deleteReply: (replyId: string) => Promise<void>;
}

export function useBookClubDiscussions(chatId?: string): UseBookClubDiscussionsResult {
  const { user } = useAuth();
  const { toast } = useToast();
  const [discussions, setDiscussions] = useState<BookClubDiscussion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDiscussions = useCallback(async () => {
    if (!chatId) {
      setDiscussions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch discussions
      const { data: discussionsData, error } = await supabase
        .from('book_club_discussions')
        .select('*')
        .eq('chat_id', chatId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!discussionsData || discussionsData.length === 0) {
        setDiscussions([]);
        setLoading(false);
        return;
      }

      // Fetch creators
      const creatorIds = [...new Set(discussionsData.map(d => d.created_by))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', creatorIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Fetch reply counts
      const discussionIds = discussionsData.map(d => d.id);
      const { data: replyCounts } = await supabase
        .from('book_club_discussion_replies')
        .select('discussion_id')
        .in('discussion_id', discussionIds);

      const countMap = new Map<string, number>();
      replyCounts?.forEach(r => {
        countMap.set(r.discussion_id, (countMap.get(r.discussion_id) || 0) + 1);
      });

      const enriched: BookClubDiscussion[] = discussionsData.map(d => ({
        ...d,
        type: d.type as 'prompt' | 'chapter',
        creator: profileMap.get(d.created_by) as BookClubDiscussion['creator'],
        reply_count: countMap.get(d.id) || 0,
      }));

      setDiscussions(enriched);
    } catch (err) {
      console.error('Error fetching discussions:', err);
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const createDiscussion = async (data: {
    type: 'prompt' | 'chapter';
    title: string;
    description?: string;
    chapter_start?: number;
    chapter_end?: number;
    is_spoiler?: boolean;
  }) => {
    if (!chatId || !user) return;

    const maxSort = discussions.length > 0
      ? Math.max(...discussions.map(d => d.sort_order)) + 1
      : 0;

    const { error } = await supabase
      .from('book_club_discussions')
      .insert({
        chat_id: chatId,
        created_by: user.id,
        type: data.type,
        title: data.title,
        description: data.description || null,
        chapter_start: data.chapter_start || null,
        chapter_end: data.chapter_end || null,
        is_spoiler: data.is_spoiler ?? (data.type === 'chapter'),
        sort_order: maxSort,
      });

    if (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Could not create discussion.' });
      throw error;
    }

    toast({ title: data.type === 'chapter' ? 'Chapter thread created' : 'Discussion prompt posted' });
    await fetchDiscussions();
  };

  const deleteDiscussion = async (id: string) => {
    const { error } = await supabase
      .from('book_club_discussions')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Could not delete discussion.' });
      return;
    }

    setDiscussions(prev => prev.filter(d => d.id !== id));
  };

  const fetchReplies = async (discussionId: string): Promise<BookClubDiscussionReply[]> => {
    const { data, error } = await supabase
      .from('book_club_discussion_replies')
      .select('*')
      .eq('discussion_id', discussionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching replies:', error);
      return [];
    }

    if (!data || data.length === 0) return [];

    const userIds = [...new Set(data.map(r => r.user_id))];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, name, image')
      .in('id', userIds);

    const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

    return data.map(r => ({
      ...r,
      user: profileMap.get(r.user_id) as BookClubDiscussionReply['user'],
    }));
  };

  const addReply = async (discussionId: string, content: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('book_club_discussion_replies')
      .insert({
        discussion_id: discussionId,
        user_id: user.id,
        content,
      });

    if (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Could not post reply.' });
      throw error;
    }

    // Update reply count locally
    setDiscussions(prev =>
      prev.map(d =>
        d.id === discussionId
          ? { ...d, reply_count: (d.reply_count || 0) + 1 }
          : d
      )
    );
  };

  const deleteReply = async (replyId: string) => {
    const { error } = await supabase
      .from('book_club_discussion_replies')
      .delete()
      .eq('id', replyId);

    if (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Could not delete reply.' });
    }
  };

  return {
    discussions,
    loading,
    createDiscussion,
    deleteDiscussion,
    fetchReplies,
    addReply,
    deleteReply,
  };
}
